import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { Gift, Copy, Share2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

interface ReferralDialogProps {
  userId: string;
  controlledOpen?: boolean;
  onControlledOpenChange?: (open: boolean) => void;
}

export const ReferralDialog = ({ userId, controlledOpen, onControlledOpenChange }: ReferralDialogProps) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? (onControlledOpenChange ?? (() => {})) : setInternalOpen;

  const { data: profile } = useQuery({
    queryKey: ["referral-profile", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("referral_code, username")
        .eq("id", userId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!userId && open,
  });

  const { data: referralStats } = useQuery({
    queryKey: ["referral-stats", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("referrals")
        .select("*")
        .eq("referrer_user_id", userId)
        .eq("credits_awarded", true);
      if (error) throw error;
      
      return { completed: data?.length || 0 };
    },
    enabled: !!userId && open,
  });

  const referralCode = profile?.referral_code || "";

  const copyReferralCode = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode);
      toast.success(t('referral_dialog.code_copied'));
    }
  };

  const shareReferralCode = async () => {
    if (!referralCode) return;
    
    const shareData = {
      title: t('referral_dialog.share_title'),
      text: t('referral_dialog.share_text', { code: referralCode, url: window.location.origin }),
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        copyReferralCode();
      }
    } catch (err) {
      if ((err as Error).name !== "AbortError") {
        copyReferralCode();
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          <Button variant="link" className="gap-2 text-green-600 hover:text-green-700 p-0 h-auto font-normal">
            <Gift className="w-3.5 h-3.5" />
            {t('referral_dialog.get_free')}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Gift className="w-5 h-5 text-primary flex-shrink-0" />
            <span className="break-words">{t('referral_dialog.title')}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* How it works */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-sm">{t('referral_dialog.how_it_works')}</h3>
            <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside break-words">
              <li>{t('referral_dialog.step_1')}</li>
              <li className="break-words">{t('referral_dialog.step_2')}</li>
              <li>{t('referral_dialog.step_3')}</li>
              <li className="break-words"><strong>{t('referral_dialog.step_4')}</strong></li>
            </ol>
          </div>

          {/* Stats */}
          {referralStats && (
            <div className="text-center p-4 bg-muted/30 rounded-lg">
              <Gift className="w-6 h-6 mx-auto mb-2 text-green-600" />
              <div className="text-3xl font-bold text-green-600">{referralStats.completed}/5</div>
              <div className="text-sm text-muted-foreground">{t('referral_dialog.completed_referrals')}</div>
            </div>
          )}

          {/* Referral Code */}
          <div className="space-y-2 min-w-0">
            <label className="text-sm font-medium">{t('referral_dialog.your_code')}</label>
            <div className="flex gap-2 min-w-0">
              <div className="flex-1 bg-muted px-3 py-2 rounded-md text-lg font-mono font-bold text-center min-w-0 overflow-hidden">
                {referralCode || t('referral_dialog.loading')}
              </div>
              <Button 
                size="sm" 
                variant="outline"
                onClick={copyReferralCode}
                disabled={!referralCode}
                className="flex-shrink-0"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Share Button */}
          <Button 
            className="w-full gap-2" 
            onClick={shareReferralCode}
            disabled={!referralCode}
          >
            <Share2 className="w-4 h-4" />
            {t('referral_dialog.share_button')}
          </Button>

          <p className="text-xs text-muted-foreground text-center break-words">
            {t('referral_dialog.credits_awarded_note')}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
